import type { WatermarkConfig, WatermarkPosition } from "../api";

/**
 * Effective watermark settings for a single scene = global defaults
 * (`item.watermark`) merged with per-scene overrides under
 * `scene.inputs.extras.wm*`. Per-scene values always win when present;
 * unset fields fall back to the global defaults.
 *
 * Per-scene keys (all flat, because `SceneInputs.extras` only allows
 * primitive values):
 *   - wmPosition  : "tl" | "tr" | "bl" | "br"
 *   - wmOpacity   : 0..1
 *   - wmSize      : 24..400 (logo height in composition px)
 *   - wmPaddingPx : corner distance
 *   - wmOffsetX/Y : optional fine-tune in % of canvas
 *   - wmXPct/YPct : optional free centre coordinate (overrides corner anchor)
 */
export const SCENE_WM_KEYS = [
  "wmPosition",
  "wmOpacity",
  "wmSize",
  "wmPaddingPx",
  "wmOffsetX",
  "wmOffsetY",
  "wmXPct",
  "wmYPct",
] as const;
export type SceneWatermarkKey = (typeof SCENE_WM_KEYS)[number];

export type EffectiveWatermark = Pick<
  WatermarkConfig,
  "position" | "opacity" | "size" | "paddingPx" | "offsetX" | "offsetY" | "xPct" | "yPct"
>;

const isPos = (s: unknown): s is WatermarkPosition =>
  s === "tl" || s === "tr" || s === "bl" || s === "br";

const numOr = (
  ex: Record<string, string | number | boolean> | undefined,
  k: string,
  fallback: number | undefined,
): number | undefined => {
  const v = ex?.[k];
  return typeof v === "number" ? v : fallback;
};

export function resolveSceneWatermark(
  global: WatermarkConfig,
  extras: Record<string, string | number | boolean> | undefined,
): EffectiveWatermark {
  const pos = extras?.wmPosition;
  return {
    position: isPos(pos) ? pos : global.position,
    opacity: numOr(extras, "wmOpacity", global.opacity) ?? global.opacity,
    size: numOr(extras, "wmSize", global.size) ?? global.size,
    paddingPx: numOr(extras, "wmPaddingPx", global.paddingPx) ?? global.paddingPx,
    offsetX: numOr(extras, "wmOffsetX", global.offsetX),
    offsetY: numOr(extras, "wmOffsetY", global.offsetY),
    xPct: numOr(extras, "wmXPct", global.xPct),
    yPct: numOr(extras, "wmYPct", global.yPct),
  };
}

/** True when the scene has at least one per-scene watermark override. */
export function hasSceneWatermarkOverride(
  extras: Record<string, string | number | boolean> | undefined,
): boolean {
  if (!extras) return false;
  for (const k of SCENE_WM_KEYS) {
    if (extras[k] !== undefined) return true;
  }
  return false;
}

/**
 * Scenes whose design already contains a prominent brand logo. We mirror
 * the engine list (`src/hyperframes/reel/index.ts → SCENES_WITH_OWN_LOGO`)
 * so the Inspector's „Logo in dieser Scene" toggle reflects the actual
 * default render state of the watermark for these scenes.
 *
 * Keep this list in sync with the engine.
 */
export const SCENES_WITH_OWN_LOGO: ReadonlySet<string> = new Set([
  "signOff",
  "logoStamp",
  "logoBeamStreak",
  "logoCircleSweep",
]);

/**
 * Effective watermark visibility for a single scene, mirroring
 * `reel/index.ts` show-logic so the Inspector's toggle state stays
 * truthful: returns `true` when the per-clip watermark would actually
 * render in the composition, `false` otherwise.
 */
export function isSceneWatermarkVisible(
  sceneId: string,
  extras: Record<string, string | number | boolean> | undefined,
  globalEnabled: boolean,
): boolean {
  if (!globalEnabled) return false;
  const flag = extras?.hideLogo;
  if (flag === true) return false;
  if (flag === false) return true;
  return !SCENES_WITH_OWN_LOGO.has(sceneId);
}
